import { useCallback, useEffect, useState } from 'react';
import type {
  VendorAlert,
  VendorPortalOverview as Overview,
  VendorVerificationStatus,
  VerificationType,
} from '@mvcp/contracts';
import { getVendorPortalOverview } from '../api';
import { CertificateRows } from './CertificateRows';
import { DeclareCertificateForm } from './DeclareCertificateForm';
import { Skeleton } from './Skeleton';
import { StatusPill } from './statusPill';
import { FormModal } from './FormModal';

/**
 * Verification-status → pill tone + label. Inlined here (contracts is the source of
 * truth) because the web consumes `@mvcp/contracts` as TYPES ONLY — importing a
 * runtime value breaks `vite build` (Story 1.6). Mirrors VendorDirectory.tsx.
 */
const VERIFICATION_TONE: Record<VendorVerificationStatus, 'ok' | 'warn' | 'bad'> = {
  verified: 'ok',
  pending: 'warn',
  unverified: 'bad',
};
const VERIFICATION_LABEL: Record<VendorVerificationStatus, string> = {
  verified: 'Verified',
  pending: 'Pending',
  unverified: 'Unverified',
};

function VerificationPill({ status }: { status: VendorVerificationStatus }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={VERIFICATION_TONE[status]}>
      <span className="status-pill__dot" aria-hidden="true" />
      {VERIFICATION_LABEL[status]}
    </span>
  );
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; overview: Overview }
  | { kind: 'error'; message: string };

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Vendor self-service portal → Overview (Story 5.1, FR-1/FR-22/UX-DR9). Renders the
 * Vendor's OWN profile, global verification status, an aggregate-or-suppressed usage
 * line, an alerts panel (own expiring/pending + incoming requested items), and its
 * company-level & device-level Certificate sections. NO customer identity is ever
 * rendered — the payload carries none by construction.
 */
export function VendorPortalOverview(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const overview = await getVendorPortalOverview();
      setState({ kind: 'ready', overview });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Vendor Portal
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Your certifications, verification status, and alerts. Customer identities are never shown.
        </p>
      </div>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading overview">
          <Skeleton height={20} width="40%" />
          <Skeleton height={48} width="60%" />
          <Skeleton height={90} />
          <Skeleton height={90} />
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load your overview</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && <ReadyOverview overview={state.overview} onReload={() => void load()} />}
    </>
  );
}

function ReadyOverview({
  overview,
  onReload,
}: {
  overview: Overview;
  onReload: () => void;
}): React.JSX.Element {
  const { profile, usage, alerts, companyCertificates, deviceCertificates } = overview;
  const [showDeclare, setShowDeclare] = useState(false);
  // Framework id → short code, from the vendor-safe map the payload carries (the
  // GLOBAL framework catalog, no customer identity). Falls back to the id only if a
  // code is somehow missing, so a row never renders blank.
  const frameworkCode = (id: string): string => overview.frameworks[id] ?? id;
  // Framework id → verification_type (the orthogonal provenance marker). Undefined
  // when unmapped → the badge simply does not render (Epic 3/4 surfaces stay bare).
  const frameworkVerificationType = (id: string): VerificationType | undefined =>
    overview.frameworkVerificationTypes[id];
  // Split the Vendor's own declared (proposed) certs into the same company/device
  // sections, rendered as "Declared · pending review" rows.
  const declaredCompany = overview.declaredCertificates.filter((d) => d.subjectType === 'vendor');
  const declaredDevice = overview.declaredCertificates.filter((d) => d.subjectType === 'device');

  return (
    <>
      {/* Profile card */}
      <section className="card stack" aria-labelledby="profile-heading">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="profile-heading" style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
            {profile.name}
          </h2>
          <VerificationPill status={profile.verificationStatus} />
        </div>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Category</div>
            <div style={{ fontWeight: 600 }}>{profile.category ?? '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Country</div>
            <div style={{ fontWeight: 600 }}>{profile.country ?? '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Devices</div>
            <div className="kpi-number" data-numeric>{profile.deviceCount}</div>
          </div>
        </div>
        {/* Aggregate-only usage — or a muted privacy note when suppressed. */}
        <div>
          <div className="muted" style={{ fontSize: 13 }}>Usage</div>
          {usage.suppressed ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Hidden to protect customer privacy.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Used across <span data-numeric style={{ fontWeight: 700 }}>{usage.companyCount}</span>{' '}
              {usage.companyCount === 1 ? 'company' : 'companies'} /{' '}
              <span data-numeric style={{ fontWeight: 700 }}>{usage.siteCount}</span>{' '}
              {usage.siteCount === 1 ? 'site' : 'sites'}
            </p>
          )}
        </div>
      </section>

      {/* Alerts panel */}
      <section className="notification-panel card" aria-labelledby="alerts-heading">
        <div className="notification-panel__head">
          <h2 id="alerts-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Alerts</h2>
        </div>
        {alerts.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nothing needs your attention right now.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alerts.map((a) => (
              <li
                key={alertKey(a)}
                className="notification-item"
                style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 8 }}
              >
                <AlertRow alert={a} frameworkCode={frameworkCode} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Declare a certificate (Story 5.2) */}
      <section className="card stack" aria-labelledby="declare-heading">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="declare-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Declare a certificate
          </h2>
          {!showDeclare && (
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => setShowDeclare(true)}
              style={{ marginLeft: 'auto' }}
            >
              Declare a certificate
            </button>
          )}
        </div>
        {showDeclare ? (
          <FormModal title="Declare a certificate" titleId="declare-cert-heading" onClose={() => setShowDeclare(false)} wide>
          <DeclareCertificateForm
            declarableFrameworks={overview.declarableFrameworks}
            devices={overview.devices}
            onCreated={() => {
              setShowDeclare(false);
              onReload();
            }}
            onCancel={() => setShowDeclare(false)}
          />
          </FormModal>
        ) : (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Upload evidence and declare a certificate for platform review. It stays
            “Declared · pending review” until a platform administrator verifies it.
          </p>
        )}
      </section>

      {/* Company-level Certificates */}
      <section className="card stack" aria-labelledby="company-certs-heading">
        <h2 id="company-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Company Certificates
        </h2>
        <CertificateRows
          certificates={companyCertificates}
          declared={declaredCompany}
          frameworkCode={frameworkCode}
          frameworkVerificationType={frameworkVerificationType}
        />
      </section>

      {/* Device-level Certificates */}
      <section className="card stack" aria-labelledby="device-certs-heading">
        <h2 id="device-certs-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Device Certificates
        </h2>
        <CertificateRows
          certificates={deviceCertificates}
          declared={declaredDevice}
          frameworkCode={frameworkCode}
          frameworkVerificationType={frameworkVerificationType}
        />
      </section>
    </>
  );
}

/** A stable key for an alert row (no server id on requested alerts). */
function alertKey(a: VendorAlert): string {
  if (a.kind === 'requested') {
    return `req-${a.frameworkId}-${a.subjectType}-${a.deviceId ?? 'self'}`;
  }
  return `${a.kind}-${a.certificateId}`;
}

function AlertRow({
  alert,
  frameworkCode,
}: {
  alert: VendorAlert;
  frameworkCode: (frameworkId: string) => string;
}): React.JSX.Element {
  if (alert.kind === 'requested') {
    return (
      <>
        <span className="status-pill" data-tone="warn">
          <span className="status-pill__dot" aria-hidden="true" />
          Requested
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} data-numeric>
          {frameworkCode(alert.frameworkId)}
        </span>
        <span className="muted" style={{ fontSize: 13 }}>
          {alert.subjectType === 'device' ? 'Device' : 'Company'} · {shortDate(alert.requestedAt)}
        </span>
      </>
    );
  }
  return (
    <>
      <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>{alert.kind}</span>
      <StatusPill status={alert.status} />
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} data-numeric>
        {frameworkCode(alert.frameworkId)}
      </span>
      <span className="muted" style={{ fontSize: 13 }}>{shortDate(alert.expiresAt)}</span>
    </>
  );
}
